import type { LocationQuery } from 'vue-router';
import { buildUrlString } from './buildUrlString';
import { QUERY_PARAM_LOGGEDIN, QUERY_PARAM_SIGNUP } from '~~/lib/constants';

/**
 * Builds the authentication login URL with all required parameters.
 * This centralizes the login URL construction to avoid code duplication.
 *
 * @param redirectPath - The path to redirect to after login
 * @param queryParams - Optional query parameters to include in the redirect URL
 * @param promptType - The prompt type: 'login' (default) or 'create' (for registration)
 * @param withAbsolutePath - If true, the protocol+host will be prepended
 * @returns The complete login URL string
 */
export function buildAuthLoginUri(
  redirectPath: string,
  queryParams?:
    URLSearchParams | Record<string, string> | LocationQuery | undefined,
  promptType: 'login' | 'create' = 'login',
  withAbsolutePath: string | boolean = true
): string {
  // Create a copy of query params and add the appropriate flag based on prompt type
  const loginRedirectQueryParams = new URLSearchParams(
    queryParams as Record<string, string>
  );

  // Use signUp flag for registration, loggedIn flag for login
  if (promptType === 'create') {
    loginRedirectQueryParams.append(QUERY_PARAM_SIGNUP, '');
  } else {
    loginRedirectQueryParams.append(QUERY_PARAM_LOGGEDIN, '');
  }

  // Build the complete redirect URL with query parameters
  const redirectUrl =
    redirectPath +
    (loginRedirectQueryParams.toString().length > 0
      ? '?' + loginRedirectQueryParams.toString()
      : '');

  // Build the final login URL with redirect and prompt parameters
  return buildUrlString(
    '',
    '/auth/entra/login',
    `redirect=${encodeURIComponent(redirectUrl)}&prompt=${promptType}`,
    undefined,
    withAbsolutePath,
    false
  );
}
